<script setup lang="ts">
import { ref, computed } from 'vue'
import type { BuildingBlockDto } from '@/stores/buildingBlock'
import { matches } from '@/utils/strings'
import DefaultIcon from '@/components/app/DefaultIcon.vue'

interface Category {
  id: number
  name: string
  blocks: BuildingBlockDto[]
}

const props = defineProps<{
  categories: Category[]
  readonly: boolean
}>()

const emit = defineEmits<{
  addBlock: [category: Category, block: BuildingBlockDto]
  deleteBlock: [category: Category, block: BuildingBlockDto]
}>()

const searchQuery = ref('')
const currentSelections = ref<
  Array<{
    categoryId: number
    blockId: number
  }>
>([])

// Using shared string utilities for normalization and matching

const filteredCategories = computed(() => {
  if (!searchQuery.value) return props.categories
  const searchText = searchQuery.value

  return props.categories.filter((category: Category) => {
    if (matches(category.name, searchText)) return true
    return category.blocks.some((block: BuildingBlockDto) => matches(block.name, searchText))
  })
})

const filteredBlocks = (category: Category): BuildingBlockDto[] => {
  if (!searchQuery.value) return category.blocks
  const searchText = searchQuery.value
  return category.blocks.filter((block) => matches(block.name, searchText))
}

const isSelected = (categoryId: number, blockId: number) => {
  return currentSelections.value.some(
    (selection) => selection.categoryId === categoryId && selection.blockId === blockId
  )
}

const selectBlock = (category: Category, block: BuildingBlockDto) => {
  const selectionIndex = currentSelections.value.findIndex(
    (selection) => selection.categoryId === category.id && selection.blockId === block.id
  )

  if (selectionIndex >= 0) {
    currentSelections.value.splice(selectionIndex, 1)
  } else {
    currentSelections.value.push({
      categoryId: category.id,
      blockId: block.id
    })
  }
}

const addCurrentSelection = () => {
  if (currentSelections.value.length === 0) return

  for (const selection of currentSelections.value) {
    const { categoryId, blockId } = selection
    const category = props.categories.find(
      (categoryItem: Category) => categoryItem.id === categoryId
    )
    if (!category || !blockId) continue

    const block = category.blocks.find(
      (buildingBlock: BuildingBlockDto) => buildingBlock.id === blockId
    )
    if (!block) continue

    emit('addBlock', category, block)
  }

  // Clear selections after adding
  currentSelections.value = []
}

const deleteCurrentSelection = () => {
  if (currentSelections.value.length === 0) return

  // Process all selections
  for (const selection of currentSelections.value) {
    const { categoryId, blockId } = selection
    const category = props.categories.find(
      (categoryItem: Category) => categoryItem.id === categoryId
    )
    if (!category || !blockId) continue

    const block = category.blocks.find(
      (buildingBlock: BuildingBlockDto) => buildingBlock.id === blockId
    )
    if (!block) continue

    emit('deleteBlock', category, block)
  }

  // Clear selections after deleting
  currentSelections.value = []
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-4">
      <h2 class="text-h6">Available building blocks</h2>
      <div class="d-flex gap-2">
        <v-btn
          v-if="
            currentSelections.length > 0 &&
            currentSelections.every(
              (selection) =>
                props.categories.find((category) => category.id === selection.categoryId)?.id === 0
            )
          "
          :disabled="currentSelections.length === 0 || props.readonly"
          @click="deleteCurrentSelection"
          prepend-icon="mdi-delete"
          class="mr-2"
        >
          Delete{{ currentSelections.length > 1 ? ` (${currentSelections.length})` : '' }}
        </v-btn>
        <v-btn
          :disabled="currentSelections.length === 0 || props.readonly"
          @click="addCurrentSelection"
          prepend-icon="mdi-plus"
        >
          Add{{ currentSelections.length > 1 ? ` (${currentSelections.length})` : '' }}
        </v-btn>
      </div>
    </div>

    <v-text-field
      v-model="searchQuery"
      prepend-inner-icon="mdi-magnify"
      placeholder="Search..."
      variant="outlined"
      density="compact"
      hide-details
      class="mb-4"
    />

    <v-card variant="outlined" class="pa-0" min-height="260">
      <v-list density="compact" class="py-1">
        <template v-for="category in filteredCategories" :key="category.id">
          <!-- Category row -->
          <v-list-item class="bb-category-row" :ripple="false">
            <template #prepend>
              <v-icon size="small" color="cyan" class="mr-2"> mdi-minus </v-icon>
            </template>

            <v-list-item-title class="font-weight-medium">
              {{ category.name }}
            </v-list-item-title>
          </v-list-item>

          <!-- Blocks under category (only render wrapper when category has blocks) -->
          <template v-if="category.blocks.length">
            <v-list-item
              v-for="block in filteredBlocks(category)"
              :key="block.id"
              class="bb-block-row px-6"
              :class="{
                'bb-block-row--active': isSelected(category.id, block.id)
              }"
              :ripple="false"
              @click="selectBlock(category, block)"
            >
              <template #prepend>
                <v-icon size="x-small" class="mr-2" v-if="!block.hidden"> mdi-copyright </v-icon>
                <default-icon v-if="block.hidden" />
              </template>

              <v-list-item-title>
                {{ block.name }}
              </v-list-item-title>
            </v-list-item>
          </template>
        </template>
      </v-list>
    </v-card>
  </div>
</template>

<style scoped>
.bb-category-row {
  min-height: 36px;
}

.bb-block-row {
  min-height: 32px;
  padding-left: 40px !important;
}

.bb-block-row--active {
  background-color: #b2ebf2;
}
</style>
